import { createElement, type ReactElement } from 'react';
import type { IconBaseProps } from 'react-icons';
import { BiFile, BiImage, BiVideo } from 'react-icons/bi';
import { LuFileQuestion } from 'react-icons/lu';

const PREVIEWABLE_MIME = new Set([
  'application/pdf',
  'text/csv',
  'text/plain',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
]);

// Mirrors the viewer ladder in DocumentPreview.tsx (lines 62-84).
// Add an extension here whenever a new viewer is wired there.
const PREVIEWABLE_EXT = new Set([
  'PDF',
  'CSV',
  'TXT',
  'DOCX',
  'XLSX',
  'XLS',
  'PNG',
  'JPG',
  'JPEG',
  'GIF',
  'WEBP',
  'SVG',
  'HEIC',
  'BMP',
  'MP4',
  'MOV',
  'WEBM',
  'MP3',
  'WAV',
  'M4A',
]);

/** Uppercase extension from a file name, or undefined when there is no dot. */
export const fileExtension = (name: string): string | undefined => {
  const dot = name.lastIndexOf('.');
  return dot > 0 && dot < name.length - 1 ? name.slice(dot + 1).toUpperCase() : undefined;
};

// True when either MIME or extension places the file in reach of a modal viewer.
// A known-bad extension (DWG, ZIP …) rejects even when the MIME says image/;
// an unknown extension (no dot) falls through to the MIME branches.
export const canPreview = (name: string, contentType?: string): boolean => {
  const ext = fileExtension(name);
  if (ext && !PREVIEWABLE_EXT.has(ext)) return false;
  if (contentType?.startsWith('image/')) return true;
  if (contentType?.startsWith('video/')) return true;
  if (contentType && PREVIEWABLE_MIME.has(contentType)) return true;
  return ext !== undefined && PREVIEWABLE_EXT.has(ext);
};

// The one icon a file list should render for a file. Non-previewable → the
// neutral "?" glyph, matching the "can't be previewed" modal state (see
// DocumentPreview.tsx:85 and MGVN-20966 screenshots 04/05).
// Previewable images → BiImage, videos → BiVideo, everything else → BiFile.
// Returns a rendered element (not a component type) so call sites never hold a
// component in a render-scoped variable — react-hooks/static-components flags that.
// Callers own size, color, aria-hidden (via props) and any wrapper.
export const fileTypeIcon = (
  name: string,
  contentType?: string,
  props: IconBaseProps = {},
): ReactElement => {
  const Icon = !canPreview(name, contentType)
    ? LuFileQuestion
    : contentType?.startsWith('image/')
      ? BiImage
      : contentType?.startsWith('video/')
        ? BiVideo
        : BiFile;
  return createElement(Icon, props);
};
